// MACHINERY V2 (Stroje) — the add-from-catalog FORM BODY (split out of
// CatalogAddDialogV2.tsx at M2.16c).
//
// >>> WHY THIS FILE EXISTS. <<< CatalogAddDialogV2 stood at 288 of the project's
// 300-line cap and M2.16c must give it a second SHELL: the desktop dialog stays,
// and below 1280 the same form becomes a full-screen sheet (board 18,
// mobile/screenshots/18-katalog-add-sheet.png). The near-cap rule makes the
// split the first task, and the seam is the obvious one — WHAT THE FORM
// CONTAINS lives here, WHAT WRAPS IT lives in CatalogAddDialogV2.
//
// >>> ONE FORM, TWO SHELLS — WHICH IS THE WHOLE POINT. <<< The validation rules,
// the field order, the RHF registration and the submit path are shared by
// construction, so a serial that is valid in the dialog is valid in the sheet.
// The two props below are the ONLY differences, and both default to the desktop
// behaviour so the dialog's output is byte-identical to what it was before the
// split.
//
// >>> THE TOUCH FLOOR IS TWO SEPARATE FIXES, AND ONE OF THEM IS NOT HERE
// (M2.16c review, finding F5). <<< This form reuses the DESKTOP control set
// inside a phone-sized sheet, so two of its targets were under the two-axis
// 44px law:
//   · `.mv2-input` is a fixed 38px — fixed in forms/formFields.scss, where the
//     class lives, with a below-1280 `min-height` that leaves desktop alone;
//   · the "Zmenit stroj" button below is 28px, and it is inline, so it is
//     fixed here — which is why this file now reads the module's below-desktop
//     query. It renders only when `onBack` is passed (the fleet's add flow
//     does; the katalog's two entry points do not), and that flow reaches a
//     phone through the same sheet.
//
// >>> THREE EDITABLE FIELDS — AND THE BOARD'S FOUR ARE NOT THEM. <<<
// Board 18 draws "Interny nazov stroja", "Seriove cislo (S/N)", "Lokacia" and
// "Linka / oddelenie". The shipped contract is serialNumber (required, max 50) ·
// productionYear (4 digits, 1900..now) · locationId — §11's prefill table, and
// what `useCatalogAddForm` submits. An internal name and a line/department are
// not fields `ChangeMachine` carries, so drawing them would be four inputs of
// which two could never be saved. The sheet therefore renders THE DIALOG'S
// fields, per the sub-phase brief ("same fields as the desktop dialog"). S/N is
// mono on both, which is the board's own instruction and was already true.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { integerRegex } from "../../../../shared/validators";
import { formatManufacturerWithHierarchy } from "../../../../shared/utils/machineUtils";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";
import { CatalogModelListItem } from "../../models/models";
import { TextRow, TwoCol } from "../add/customMachineFieldAtoms";
import RetryNotice from "../feedback/RetryNotice";
import FormField from "../forms/FormField";
import FormSelect from "../forms/FormSelect";
import { CalendarGlyph, HashGlyph, NoteGlyph } from "../forms/FormGlyphs";
import { locationOptions } from "../add/customMachineOptions";
import { CatalogThumb, CategoryBadge, catalogThumbUrl } from "./catalogAtoms";
import useCatalogAddForm from "./useCatalogAddForm";

export interface CatalogAddFieldsProps {
  formId: string;
  model: CatalogModelListItem;
  f: ReturnType<typeof useCatalogAddForm>;
  /** "Zmenit stroj" — omitted when there is no way back. */
  onBack?: () => void;
  /**
   * Stack serial + year instead of the 1fr/1fr pair. Below 1280 two inputs
   * sharing ~354px of sheet width is under the comfortable width for a mono
   * serial, and board 18 draws every field full-bleed. Defaults to the desktop
   * pair so the dialog is unchanged.
   */
  stackFields?: boolean;
  /** Board 18's readiness callout, appended after the specs note. The dialog
   *  passes nothing and therefore renders nothing extra. */
  tierNote?: React.ReactNode;
}

const CatalogAddFields: React.FC<CatalogAddFieldsProps> = ({
  formId,
  model,
  f,
  onBack,
  stackFields,
  tierNote,
}) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);
  const currentYear = new Date().getFullYear();

  const manufacturerLine = model.manufacturer
    ? formatManufacturerWithHierarchy(
        model.manufacturer.name,
        model.manufacturer.parentManufacturerName
      )
    : "";

  const serialField = (
    <TextRow
      form={f.form}
      name="serialNumber"
      label={translate("myMachinery:serialNumber")}
      glyph={<HashGlyph />}
      required
      mono
      maxLength={50}
      placeholder={translate("myMachinery:v2.add.serialPlaceholder")}
      rules={{ required: translate("common:forms.required") }}
    />
  );

  const yearField = (
    <TextRow
      form={f.form}
      name="productionYear"
      label={translate("myMachinery:productionYear")}
      glyph={<CalendarGlyph />}
      mono
      maxLength={4}
      placeholder={String(currentYear)}
      rules={{
        pattern: {
          value: integerRegex,
          message: translate("myMachinery:fieldRequire4Digits"),
        },
        min: {
          value: 1900,
          message: translate("myMachinery:enterValidYearRange", { year: currentYear }),
        },
        max: {
          value: currentYear,
          message: translate("myMachinery:enterValidYearRange", { year: currentYear }),
        },
      }}
    />
  );

  return (
    <form id={formId} onSubmit={f.submit} noValidate style={{ minWidth: 0 }}>
      <div className="eyebrow" style={{ color: "var(--mod-accent)", margin: "2px 0 6px" }}>
        {translate("myMachinery:v2.catalog.selectedModel")}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: 10,
          marginBottom: 16,
          borderRadius: 9,
          background: "var(--mod-accent-soft)",
          border: "1px solid var(--mod-accent-line)",
          minWidth: 0,
        }}
      >
        <CatalogThumb
          url={catalogThumbUrl(model)}
          alt={model.modelName}
          width={62}
          height={44}
          radius={7}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            title={model.modelName}
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink)",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {model.modelName}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginTop: 3,
              minWidth: 0,
              flexWrap: "wrap",
            }}
          >
            {manufacturerLine && (
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--muted)",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {manufacturerLine}
              </span>
            )}
            <CategoryBadge code={model.categoryCode} />
          </div>
        </div>
        {onBack && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onBack}
            // >= 44px BOTH axes below 1280 — see the header. `minWidth`
            // accompanies the height because this is a flex child a growing
            // sibling could otherwise squeeze. At >= 1280 the object is the
            // 28px one it always was.
            style={
              belowDesktop
                ? { height: 44, minWidth: 44, fontSize: 12.5, flexShrink: 0 }
                : { height: 28, fontSize: 11.5, flexShrink: 0 }
            }
          >
            {translate("myMachinery:v2.catalog.changeModel")}
          </button>
        )}
      </div>

      {stackFields ? (
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          {serialField}
          {yearField}
        </div>
      ) : (
        <TwoCol>
          {serialField}
          {yearField}
        </TwoCol>
      )}

      <FormField label={translate("myMachinery:location")} htmlFor="mv2-catalog-add-location">
        <FormSelect
          id="mv2-catalog-add-location"
          placeholder={translate("myMachinery:v2.add.locationPlaceholder")}
          ariaLabel={translate("myMachinery:location")}
          value={f.locationId || undefined}
          options={locationOptions(f.locations, translate)}
          clearLabel={translate("myMachinery:v2.add.locationClear")}
          onChange={(value) => f.setLocationId(value || null)}
        />
      </FormField>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          padding: "9px 11px",
          borderRadius: 8,
          background: "var(--bg-soft)",
          border: "1px solid var(--border-soft)",
          fontSize: 11.5,
          color: "var(--muted)",
          minWidth: 0,
        }}
      >
        <span style={{ color: "var(--mod-accent)", flexShrink: 0, marginTop: 1 }}>
          <NoteGlyph />
        </span>
        <span style={{ minWidth: 0 }}>
          {translate("myMachinery:v2.catalog.specsInheritedNote")}
        </span>
      </div>

      {tierNote}

      {/* RULE (d) / §9 D7 — a failed submit renders HERE and touches no field.
          `useCatalogAddForm`'s catch sets `submitError` and returns, so the
          serial, the year and the location are all still on screen and
          `onRetry` re-submits the very same values. */}
      {f.submitError && (
        <div style={{ marginTop: 12 }}>
          <RetryNotice
            title={f.submitError}
            hint={translate("myMachinery:v2.add.createFailedHint")}
            onRetry={f.submit}
            busy={f.submitting}
          />
        </div>
      )}
    </form>
  );
};

export default CatalogAddFields;
